/**
 * Shared scoring-rubric DATA (T23) — the single source for the band/score rubrics that BOTH the
 * engine (`services/idea-engine`) and the live prompts (`services/idea-engine-api`) need. Services
 * are isolated (only `@squadlab/contracts` is shared, ADR 0001), so the api can't import the engine
 * matrices — it used to RESTATE their band labels as prompt copy, which drifts the moment a label is
 * renamed. This module is the one place the labels (and their criteria + rates) live: the engine
 * builds its scorecard matrices over these rows, and the api derives its prompt rubric lines from
 * them, so a rename lands in both at once.
 *
 * Data only — no logic (contracts is a pure types + framework-data package). The engine adds its
 * UI-only fields (a metric's `question`/`rationale`, a factor's `weight`/`whyWeight`); those never
 * appear in the api prompts, so they stay engine-side and can't drift.
 */

// ── 0–4 band-matrix stages (QSAM / AOM / RAM / winRate) ──────────────────────────────────────────

/** One band of a 0–4 band-matrix stage: a qualitative criterion mapped to a [0,1] rate. */
export interface BandRubricRow {
  readonly label: string;
  readonly criteria: string;
  /** The [0,1] rate this band contributes to the stage product. */
  readonly rate: number;
}

/** A band-matrix metric: its key, display label, assumption field, and bands ordered best→worst (level 4..0). */
export interface BandRubricMetric {
  readonly key: string;
  readonly label: string;
  readonly field: string;
  readonly bands: readonly BandRubricRow[];
}

/** QSAM — five qualification filters (schema v7). Rates are the qualification fraction each band contributes. */
export const QSAM_RUBRIC: readonly BandRubricMetric[] = [
  {
    key: 'pain',
    label: 'Pain',
    field: 'painLevel',
    bands: [
      { label: 'Acute', criteria: 'Urgent top-3 problem; already spending time/money to solve it', rate: 0.9 },
      { label: 'Strong', criteria: "Recognised, costly problem they'd prioritise this year", rate: 0.7 },
      { label: 'Moderate', criteria: 'Real but tolerable — a "nice to fix"', rate: 0.5 },
      { label: 'Mild', criteria: 'Minor annoyance, rarely top of mind', rate: 0.3 },
      { label: 'Negligible', criteria: 'No felt pain; latent or invented problem', rate: 0.1 },
    ],
  },
  {
    key: 'budgetFit',
    label: 'Budget fit',
    field: 'budgetFitLevel',
    bands: [
      { label: 'Funded', criteria: 'Existing budget line; price trivial vs the value', rate: 0.9 },
      { label: 'Affordable', criteria: 'Clear ROI; budget findable without a fight', rate: 0.75 },
      { label: 'Stretch', criteria: 'Justifiable but needs a business case', rate: 0.5 },
      { label: 'Tight', criteria: 'Price strains the budget; a hard sell', rate: 0.3 },
      { label: 'No budget', criteria: 'Nothing allocated; expects free', rate: 0.1 },
    ],
  },
  {
    key: 'dataAvailability',
    label: 'Data availability',
    field: 'dataAvailabilityLevel',
    bands: [
      { label: 'Ready', criteria: 'Already exists, accessible, clean', rate: 0.95 },
      { label: 'Obtainable', criteria: 'Exists but needs integration / cleanup', rate: 0.8 },
      { label: 'Partial', criteria: 'Some data; gaps need workarounds', rate: 0.55 },
      { label: 'Scarce', criteria: 'Little usable data; heavy collection needed', rate: 0.3 },
      { label: 'Absent', criteria: "Required data doesn't exist or is inaccessible", rate: 0.1 },
    ],
  },
  {
    key: 'legalEligibility',
    label: 'Legal eligibility',
    field: 'legalEligibilityLevel',
    bands: [
      { label: 'Unrestricted', criteria: 'No regulatory barrier; clearly permitted', rate: 1.0 },
      { label: 'Compliant', criteria: 'Permitted with standard compliance (consent / T&Cs)', rate: 0.85 },
      { label: 'Conditional', criteria: 'Allowed in some segments / regions; restrictions apply', rate: 0.6 },
      { label: 'Restricted', criteria: 'Licences / approvals required; significant hurdles', rate: 0.3 },
      { label: 'Prohibited', criteria: 'Barred or legally grey for most of the segment', rate: 0.05 },
    ],
  },
  {
    key: 'digitalReadiness',
    label: 'Digital readiness',
    field: 'digitalReadinessLevel',
    bands: [
      { label: 'Digital-native', criteria: 'Already use comparable tools daily', rate: 0.95 },
      { label: 'Capable', criteria: 'Comfortable adopting with light onboarding', rate: 0.8 },
      { label: 'Developing', criteria: 'Some friction; needs change management', rate: 0.55 },
      { label: 'Lagging', criteria: 'Low digital maturity; a real adoption barrier', rate: 0.3 },
      { label: 'Offline', criteria: 'Largely non-digital; the product cannot land', rate: 0.1 },
    ],
  },
];

/** AOM — six adoption gates (schema v8). Rates are the fraction of the qualified market that clears each gate. */
export const AOM_RUBRIC: readonly BandRubricMetric[] = [
  {
    key: 'awareness',
    label: 'Awareness',
    field: 'awarenessLevel',
    bands: [
      { label: 'Ubiquitous', criteria: 'Actively-searched category; cheap existing channels', rate: 0.75 },
      { label: 'Reachable', criteria: 'Clear channels; moderate effort to reach', rate: 0.55 },
      { label: 'Findable', criteria: 'Reachable but crowded; needs sustained marketing', rate: 0.4 },
      { label: 'Obscure', criteria: 'Hard-to-reach segment; no obvious channel', rate: 0.22 },
      { label: 'Hidden', criteria: 'Fragmented / unreachable at scale', rate: 0.1 },
    ],
  },
  {
    key: 'urgency',
    label: 'Urgency',
    field: 'urgencyLevel',
    bands: [
      { label: 'Burning', criteria: 'Acting this quarter; a fire to put out', rate: 0.7 },
      { label: 'Active', criteria: "On this year's roadmap", rate: 0.5 },
      { label: 'Planned', criteria: 'Wanted, but not prioritised', rate: 0.35 },
      { label: 'Someday', criteria: 'Vague intent, no trigger', rate: 0.2 },
      { label: 'Dormant', criteria: 'No pull; the status quo is comfortable', rate: 0.08 },
    ],
  },
  {
    key: 'trust',
    label: 'Trust',
    field: 'trustLevel',
    bands: [
      { label: 'Assured', criteria: 'Proven / credentialed; low perceived risk', rate: 0.8 },
      { label: 'Credible', criteria: 'Believable with light proof (demo, references)', rate: 0.6 },
      { label: 'Cautious', criteria: 'Needs a pilot before belief', rate: 0.4 },
      { label: 'Skeptical', criteria: '"AI won\'t work for us" resistance', rate: 0.22 },
      { label: 'Distrusted', criteria: 'Data / accuracy fears block adoption', rate: 0.1 },
    ],
  },
  {
    key: 'adoptionEase',
    label: 'Adoption ease',
    field: 'adoptionEaseLevel',
    bands: [
      { label: 'Frictionless', criteria: 'Self-serve; plugs into the existing workflow', rate: 0.85 },
      { label: 'Easy', criteria: 'Light onboarding, minimal change', rate: 0.65 },
      { label: 'Moderate', criteria: 'Real change management needed', rate: 0.45 },
      { label: 'Hard', criteria: 'Deep integration / retraining', rate: 0.25 },
      { label: 'Prohibitive', criteria: 'Rip-and-replace; heavy switching cost', rate: 0.1 },
    ],
  },
  {
    key: 'competitiveWin',
    label: 'Competitive win',
    field: 'competitiveWinLevel',
    bands: [
      { label: 'Dominant', criteria: 'Clear 10× wedge; no real alternative', rate: 0.7 },
      { label: 'Favoured', criteria: 'Wins most head-to-heads', rate: 0.5 },
      { label: 'Contested', criteria: 'Toss-up with incumbents', rate: 0.35 },
      { label: 'Underdog', criteria: 'Behind on brand / distribution', rate: 0.2 },
      { label: 'Outmatched', criteria: 'Entrenched incumbents own it', rate: 0.08 },
    ],
  },
  {
    key: 'retention',
    label: 'Retention',
    field: 'retentionLevel',
    bands: [
      { label: 'Sticky', criteria: 'Embedded in the workflow; switching hurts', rate: 0.92 },
      { label: 'Durable', criteria: 'Strong habit, low churn', rate: 0.8 },
      { label: 'Steady', criteria: 'Holds with ongoing value', rate: 0.6 },
      { label: 'Leaky', criteria: 'Nice-to-have; churns on budget cuts', rate: 0.4 },
      { label: 'Churny', criteria: 'One-and-done; no recurring pull', rate: 0.2 },
    ],
  },
];

/** RAM — six feasibility gates (schema v9). Rates are the fraction of the obtainable market that survives each gate (a risk discount). */
export const RAM_RUBRIC: readonly BandRubricMetric[] = [
  {
    key: 'technical',
    label: 'Technical',
    field: 'technicalLevel',
    bands: [
      { label: 'Proven', criteria: 'Off-the-shelf; the hard parts are already solved', rate: 1.0 },
      { label: 'Feasible', criteria: 'Buildable with known techniques; normal engineering risk', rate: 0.9 },
      { label: 'Stretch', criteria: 'Needs R&D on an unproven capability', rate: 0.7 },
      { label: 'Frontier', criteria: 'Depends on a research breakthrough', rate: 0.45 },
      { label: 'Unproven', criteria: 'No known path with current technology', rate: 0.2 },
    ],
  },
  {
    key: 'regulatory',
    label: 'Regulatory',
    field: 'regulatoryLevel',
    bands: [
      { label: 'Clear', criteria: 'No regulatory barrier to operating', rate: 1.0 },
      { label: 'Compliant', criteria: 'Operable within existing rules', rate: 0.9 },
      { label: 'Licensed', criteria: 'Needs approvals / licences that are obtainable', rate: 0.7 },
      { label: 'Contested', criteria: 'Grey area; a regulator could block it', rate: 0.4 },
      { label: 'Barred', criteria: 'Prohibited or a hostile regime', rate: 0.15 },
    ],
  },
  {
    key: 'dataRights',
    label: 'Data rights',
    field: 'dataRightsLevel',
    bands: [
      { label: 'Owned', criteria: 'First-party or public data', rate: 1.0 },
      { label: 'Licensable', criteria: 'A clear licensing path exists', rate: 0.9 },
      { label: 'Consented', criteria: 'Needs user / partner consent at scale', rate: 0.7 },
      { label: 'Restricted', criteria: 'Contested ownership or privacy limits', rate: 0.45 },
      { label: 'Blocked', criteria: 'No lawful access to the required data', rate: 0.2 },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    field: 'securityLevel',
    bands: [
      { label: 'Hardened', criteria: 'Low attack surface; standard controls suffice', rate: 1.0 },
      { label: 'Securable', criteria: 'Manageable with known practices', rate: 0.9 },
      { label: 'Sensitive', criteria: 'Handles high-value data; real exposure', rate: 0.7 },
      { label: 'Fragile', criteria: 'Large surface / high breach impact', rate: 0.45 },
      { label: 'Exposed', criteria: 'Unmitigable critical security risk', rate: 0.2 },
    ],
  },
  {
    key: 'monetization',
    label: 'Monetization',
    field: 'monetizationLevel',
    bands: [
      { label: 'Proven model', criteria: 'Clear willingness-to-pay; a working model', rate: 1.0 },
      { label: 'Chargeable', criteria: 'Value maps to a price buyers accept', rate: 0.9 },
      { label: 'Indirect', criteria: 'Value is real but capture is hard (free-rider)', rate: 0.65 },
      { label: 'Thin', criteria: 'Weak willingness-to-pay', rate: 0.4 },
      { label: 'Uncapturable', criteria: 'Value leaks; no viable model', rate: 0.15 },
    ],
  },
  {
    key: 'distribution',
    label: 'Distribution',
    field: 'distributionLevel',
    bands: [
      { label: 'Built-in', criteria: 'Owns a channel or a viral loop', rate: 1.0 },
      { label: 'Reachable', criteria: 'Standard go-to-market channels work', rate: 0.85 },
      { label: 'Effortful', criteria: 'Needs heavy sales / partnerships', rate: 0.6 },
      { label: 'Gated', criteria: 'Locked behind gatekeepers', rate: 0.35 },
      { label: 'Trapped', criteria: 'No viable path to the buyer', rate: 0.15 },
    ],
  },
];

/** winRate — five competitive fronts (schema v10), scored VERSUS the field. Rates are the head-to-head win share on each front. */
export const WIN_RUBRIC: readonly BandRubricMetric[] = [
  {
    key: 'product',
    label: 'Product',
    field: 'productLevel',
    bands: [
      { label: 'Decisive', criteria: 'Clearly better; a category-defining wedge buyers notice', rate: 0.95 },
      { label: 'Edge', criteria: 'Meaningfully better on the dimensions buyers weigh', rate: 0.8 },
      { label: 'Parity', criteria: 'Comparable; wins are situational', rate: 0.55 },
      { label: 'Behind', criteria: 'Rivals are better on what matters', rate: 0.3 },
      { label: 'Outclassed', criteria: 'Clearly inferior product', rate: 0.15 },
    ],
  },
  {
    key: 'price',
    label: 'Price',
    field: 'priceLevel',
    bands: [
      { label: 'Undercuts', criteria: 'Materially cheaper, or far more value per dollar', rate: 0.9 },
      { label: 'Favourable', criteria: 'Priced better for the value delivered', rate: 0.78 },
      { label: 'Comparable', criteria: 'Roughly at market', rate: 0.55 },
      { label: 'Premium', criteria: 'Costs more without a clear justification', rate: 0.3 },
      { label: 'Priced out', criteria: 'Far more expensive than the alternatives', rate: 0.15 },
    ],
  },
  {
    key: 'distribution',
    label: 'Distribution',
    field: 'distributionLevel',
    bands: [
      { label: 'Owned channel', criteria: 'A route to the buyer rivals cannot match', rate: 0.9 },
      { label: 'Strong reach', criteria: 'Efficient, differentiated go-to-market', rate: 0.75 },
      { label: 'Even', criteria: 'The same channels as everyone else', rate: 0.5 },
      { label: 'Disadvantaged', criteria: 'Rivals reach buyers more easily', rate: 0.3 },
      { label: 'Shut out', criteria: "No viable path against incumbents' distribution", rate: 0.15 },
    ],
  },
  {
    key: 'trust',
    label: 'Trust',
    field: 'trustLevel',
    bands: [
      { label: 'Trusted name', criteria: 'Brand / reputation edge; buyers default to us', rate: 0.9 },
      { label: 'Credible', criteria: 'Enough proof to be shortlisted on merit', rate: 0.72 },
      { label: 'Unproven', criteria: 'A new entrant; trust must be earned', rate: 0.5 },
      { label: 'Doubted', criteria: "Incumbents' trust is a real barrier", rate: 0.3 },
      { label: 'Distrusted', criteria: 'Buyers actively distrust a challenger here', rate: 0.15 },
    ],
  },
  {
    key: 'switching',
    label: 'Switching',
    field: 'switchingLevel',
    bands: [
      { label: 'Frictionless', criteria: 'Drop-in; near-zero switching cost', rate: 0.9 },
      { label: 'Easy', criteria: 'Light migration with a clear payoff', rate: 0.75 },
      { label: 'Costly', criteria: 'Real switching effort to overcome', rate: 0.5 },
      { label: 'Locked in', criteria: "Incumbents' lock-in is sticky", rate: 0.3 },
      { label: 'Trapped', criteria: 'Switching is effectively impossible', rate: 0.12 },
    ],
  },
];

// ── 0–10 score-rubric stages (IVS / defensibility) ──────────────────────────────────────────────

/** One band of a 0–10 score rubric: a state a score lands in, by its `min` threshold (checked high → low). */
export interface ScoreRubricBand {
  readonly label: string;
  /** Lowest 0–10 score that lands in this band. */
  readonly min: number;
  /** What this band means (shared clarity meaning for IVS; per-moat criteria for defensibility). */
  readonly meaning: string;
}

/**
 * IVS — the shared 0–10 clarity rubric applied to all six intuitive-value factors (one scale, since
 * every factor is a judgment of intuitive clarity). Ordered high → low.
 */
export const IVS_CLARITY_RUBRIC: readonly ScoreRubricBand[] = [
  { min: 9, label: 'Instant', meaning: 'understood in seconds — no explanation needed' },
  { min: 7, label: 'Clear', meaning: 'grasped with a moment of thought — one sentence suffices' },
  { min: 5, label: 'Needs framing', meaning: 'lands only after a short explanation' },
  { min: 3, label: 'Opaque', meaning: 'needs a paragraph of education before it clicks' },
  { min: 0, label: 'Confusing', meaning: "the consumer can't tell what it does or why it matters" },
];

/** The shared defensibility band labels + score thresholds — the same five bands across every moat. */
export const MOAT_BAND_META: readonly { readonly label: string; readonly min: number }[] = [
  { label: 'Entrenched', min: 9 },
  { label: 'Strong', min: 7 },
  { label: 'Emerging', min: 5 },
  { label: 'Nascent', min: 3 },
  { label: 'None', min: 0 },
];

/** A defensibility moat: the shared band labels/mins, with this moat's own per-band criteria. */
export interface MoatRubricDef {
  readonly key: string;
  readonly label: string;
  readonly field: string;
  readonly bands: readonly ScoreRubricBand[];
}

/** Build a moat's bands from its five best→worst criteria + the shared labels/mins. */
function moatBands(criteria: readonly [string, string, string, string, string]): readonly ScoreRubricBand[] {
  return criteria.map((c, i) => ({ label: MOAT_BAND_META[i]!.label, min: MOAT_BAND_META[i]!.min, meaning: c }));
}

/** Defensibility — seven heterogeneous moats (schema unchanged; 7 × 0–10), each with its OWN rubric. */
export const DEFENSIBILITY_RUBRIC: readonly MoatRubricDef[] = [
  {
    key: 'data',
    label: 'Data advantage',
    field: 'dataAdvantage',
    bands: moatBands([
      'Proprietary data flywheel rivals can’t replicate',
      'A real, accumulating data edge',
      'Some proprietary data, not yet compounding',
      'Mostly public data; a thin edge',
      'No data advantage — the same inputs as everyone',
    ]),
  },
  {
    key: 'workflow',
    label: 'Workflow lock-in',
    field: 'workflowLockin',
    bands: moatBands([
      'System-of-record; the workflow runs on it',
      'Embedded in the daily workflow',
      'Used regularly but replaceable',
      'Occasional, peripheral use',
      'A standalone tool with no embedding',
    ]),
  },
  {
    key: 'switching',
    label: 'Switching cost',
    field: 'switchingCost',
    bands: moatBands([
      'Prohibitive — data, integrations and retraining lock them in',
      'High — real migration effort to leave',
      'Moderate switching friction',
      'Low — easy to leave',
      'Zero switching cost',
    ]),
  },
  {
    key: 'distribution',
    label: 'Distribution control',
    field: 'distributionControl',
    bands: moatBands([
      'Own the channel rivals must pass through',
      'Privileged, hard-to-match distribution',
      'A distribution edge, not exclusive',
      'The same channels as everyone',
      'No distribution control',
    ]),
  },
  {
    key: 'brand',
    label: 'Brand trust',
    field: 'brandTrust',
    bands: moatBands([
      'The trusted default in the category',
      'A credible, recognised brand',
      'Building recognition',
      'Little brand presence',
      'Unknown — no brand equity',
    ]),
  },
  {
    key: 'network',
    label: 'Network effects',
    field: 'networkEffects',
    bands: moatBands([
      'Strong network effects; winner-take-most dynamics',
      'Real network effects at play',
      'Weak / early network effects',
      'Minimal cross-user value',
      'No network effect — single-player value',
    ]),
  },
  {
    key: 'regulatory',
    label: 'Regulatory advantage',
    field: 'regulatoryAdvantage',
    bands: moatBands([
      'Licences / compliance rivals can’t quickly obtain',
      'A real regulatory barrier in our favour',
      'Some compliance advantage',
      'A minor regulatory edge',
      'No regulatory barrier',
    ]),
  },
];
